import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sprout, LayoutDashboard, Leaf, CalendarRange, BookOpen, Sparkles, Scale, Moon, Sun, LogOut, LayoutGrid, MoreHorizontal, X, Crown, XCircle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useState } from "react";
import { ProBadge, UpgradeButton, TrialBadge } from "@/components/Upgrade";
import { CancelSubscriptionDialog, canCancel } from "@/components/CancelSubscriptionDialog";
import { formatDate } from "@/lib/plant";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard-link" },
  { to: "/sprouts", label: "My Sprouts", icon: Leaf, testId: "nav-sprouts-link" },
  { to: "/calendar", label: "Calendar", icon: CalendarRange, testId: "nav-calendar-link" },
  { to: "/planner", label: "Layout", icon: LayoutGrid, testId: "nav-planner-link" },
  { to: "/harvests", label: "Harvests", icon: Scale, testId: "nav-harvests-link" },
  { to: "/guides", label: "Grow Guides", icon: BookOpen, testId: "nav-guides-link" },
  { to: "/assistant", label: "Assistant", icon: Sparkles, testId: "nav-assistant-link" },
];

// Mobile bottom-bar: 4 primary thumb-reachable destinations + a "More" sheet for the rest.
const _byTo = (to) => NAV.find((n) => n.to === to);
const PRIMARY = [_byTo("/"), _byTo("/sprouts"), _byTo("/planner"), _byTo("/calendar")];
const SECONDARY = [_byTo("/harvests"), _byTo("/guides"), _byTo("/assistant")];
const SHORT = { "/": "Home", "/sprouts": "Sprouts", "/planner": "Layout", "/calendar": "Calendar" };

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const showCancel = canCancel(user);
  const canceledUntil = user?.subscription?.status === "canceled" && user?.is_premium ? user.subscription.current_period_end : null;
  const signOut = async () => { await logout(); navigate("/login"); };

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const initials = (user?.name || user?.email || "U").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-background/85 border-b border-border"
        data-testid="app-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-xl bg-emerald-700 flex items-center justify-center shadow-sm">
              <Sprout className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight hidden sm:block">Sprout Log</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {(user?.is_premium && ["active", "canceled"].includes(user?.subscription?.status)) || user?.role === "admin" ? (
              <ProBadge className="hidden sm:inline-flex" />
            ) : user?.subscription?.status === "trialing" ? (
              <TrialBadge user={user} className="hidden sm:inline-flex" />
            ) : (
              <UpgradeButton className="hidden sm:inline-flex h-9 px-3" />
            )}
            <button
              onClick={toggle}
              className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="h-9 w-9 rounded-full bg-emerald-700 text-white font-semibold flex items-center justify-center hover:opacity-90 transition"
                  data-testid="user-profile-menu"
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs font-normal text-muted-foreground truncate">{user?.email}</div>
                  {canceledUntil && (
                    <div className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-400" data-testid="menu-canceled-status">Pro until {formatDate(canceledUntil)} · won't renew</div>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin/sources")} data-testid="menu-signup-sources">
                    <BarChart3 className="h-4 w-4 mr-2" /> Sign-up sources
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={signOut} data-testid="logout-button" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
                {showCancel && (
                  <DropdownMenuItem onSelect={() => setCancelOpen(true)} data-testid="menu-cancel-subscription" className="text-muted-foreground">
                    <XCircle className="h-4 w-4 mr-2" /> Cancel subscription
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-24 md:pb-8">{children}</main>

      {/* Mobile "More" sheet */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="more-sheet">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 bg-background border-t border-border rounded-t-2xl p-4 pb-24 shadow-2xl animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">More</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Close" className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-secondary">
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SECONDARY.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`more-${item.testId}`}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium border ${
                      isActive(item.to)
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-300 dark:border-emerald-800"
                        : "text-foreground border-border hover:bg-secondary"
                    }`}
                  >
                    <Icon className="h-5 w-5 shrink-0" /> {item.label}
                  </Link>
                );
              })}
            </div>
            {!((user?.is_premium && ["active", "canceled"].includes(user?.subscription?.status)) || user?.role === "admin") && (
              <Link
                to="/pricing"
                onClick={() => setMobileOpen(false)}
                data-testid="more-pricing-link"
                className="mt-2 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600"
              >
                <Crown className="h-4 w-4" /> Upgrade to Sprout Pro
              </Link>
            )}
            {canceledUntil && (
              <p className="mt-3 text-center text-xs font-medium text-amber-700 dark:text-amber-400" data-testid="more-canceled-status">Pro until {formatDate(canceledUntil)} · won't renew</p>
            )}
            <button onClick={() => { setMobileOpen(false); signOut(); }} data-testid="more-logout-button"
              className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm font-medium border border-border text-destructive hover:bg-secondary">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
            {showCancel && (
              <button onClick={() => { setMobileOpen(false); setCancelOpen(true); }} data-testid="more-cancel-subscription"
                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary">
                <XCircle className="h-4 w-4" /> Cancel subscription
              </button>
            )}
          </div>
        </div>
      )}

      <CancelSubscriptionDialog open={cancelOpen} onOpenChange={setCancelOpen} />

      {/* Mobile bottom tab bar */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-background/95 backdrop-blur-md border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch justify-around">
          {PRIMARY.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`bottomnav-${item.testId}`}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[56px] text-[11px] font-medium transition-colors ${
                  active ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "scale-110" : ""} transition-transform`} />
                {SHORT[item.to] || item.label}
              </Link>
            );
          })}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="bottomnav-more"
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 min-h-[56px] text-[11px] font-medium transition-colors ${
              mobileOpen || SECONDARY.some((i) => isActive(i.to)) ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"
            }`}
          >
            <MoreHorizontal className="h-5 w-5" />
            More
          </button>
        </div>
      </nav>
    </div>
  );
}
